/**
 * Reporting periods.
 *
 * Pure date arithmetic on `YYYY-MM-DD` strings, so a period never depends on
 * the server's timezone: "today" is supplied by the caller (the organisation's
 * own calendar day) and everything else is derived from it.
 */

export const PERIOD_PRESETS = [
  'today',
  'this_week',
  'this_month',
  'month_to_date',
  'previous_month',
  'this_quarter',
  'quarter_to_date',
  'previous_quarter',
  'this_year',
  'year_to_date',
  'previous_year',
  'custom',
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  today: 'Today',
  this_week: 'This Week',
  this_month: 'This Month',
  month_to_date: 'Month to Date',
  previous_month: 'Previous Month',
  this_quarter: 'This Quarter',
  quarter_to_date: 'Quarter to Date',
  previous_quarter: 'Previous Quarter',
  this_year: 'This Year',
  year_to_date: 'Year to Date',
  previous_year: 'Previous Year',
  custom: 'Custom',
};

export interface DateRange {
  from: string;
  to: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

function parse(date: string): { y: number; m: number; d: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (!match) throw new RangeError(`"${date}" is not a YYYY-MM-DD date`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

const format = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

export function addDays(date: string, days: number): string {
  const { y, m, d } = parse(date);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return format(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}

/** Whole days from `a` to `b` (positive when b is later). */
export function daysBetween(a: string, b: string): number {
  const pa = parse(a);
  const pb = parse(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 86_400_000);
}

export function addMonths(date: string, months: number): string {
  const { y, m, d } = parse(date);
  const index = y * 12 + (m - 1) + months;
  const ny = Math.floor(index / 12);
  const nm = (index % 12) + 1;
  return format(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

const endOfMonth = (y: number, m: number) => format(y, m, daysInMonth(y, m));

/** Start of the fiscal year containing `date`. */
export function fiscalYearStart(date: string, startMonth: number): string {
  const { y, m } = parse(date);
  return format(m >= startMonth ? y : y - 1, startMonth, 1);
}

export function fiscalYearEnd(date: string, startMonth: number): string {
  return addDays(addMonths(fiscalYearStart(date, startMonth), 12), -1);
}

function fiscalQuarterStart(date: string, startMonth: number): string {
  const yearStart = fiscalYearStart(date, startMonth);
  const { y, m } = parse(date);
  const { y: sy, m: sm } = parse(yearStart);
  const monthsIn = (y - sy) * 12 + (m - sm);
  return addMonths(yearStart, Math.floor(monthsIn / 3) * 3);
}

/** Monday is the first day of the week. */
function weekStart(date: string): string {
  const { y, m, d } = parse(date);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(date, -((weekday + 6) % 7));
}

export function resolvePreset(
  preset: PeriodPreset,
  today: string,
  fiscalStartMonth: number,
  custom?: Partial<DateRange>,
): DateRange {
  const { y, m } = parse(today);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'this_week': {
      const from = weekStart(today);
      return { from, to: addDays(from, 6) };
    }
    case 'this_month':
      return { from: format(y, m, 1), to: endOfMonth(y, m) };
    case 'month_to_date':
      return { from: format(y, m, 1), to: today };
    case 'previous_month': {
      const first = addMonths(format(y, m, 1), -1);
      const p = parse(first);
      return { from: first, to: endOfMonth(p.y, p.m) };
    }
    case 'this_quarter': {
      const from = fiscalQuarterStart(today, fiscalStartMonth);
      return { from, to: addDays(addMonths(from, 3), -1) };
    }
    case 'quarter_to_date':
      return { from: fiscalQuarterStart(today, fiscalStartMonth), to: today };
    case 'previous_quarter': {
      const current = fiscalQuarterStart(today, fiscalStartMonth);
      const from = addMonths(current, -3);
      return { from, to: addDays(current, -1) };
    }
    case 'this_year':
      return { from: fiscalYearStart(today, fiscalStartMonth), to: fiscalYearEnd(today, fiscalStartMonth) };
    case 'year_to_date':
      return { from: fiscalYearStart(today, fiscalStartMonth), to: today };
    case 'previous_year': {
      const current = fiscalYearStart(today, fiscalStartMonth);
      return { from: addMonths(current, -12), to: addDays(current, -1) };
    }
    case 'custom': {
      if (!custom?.from || !custom.to) throw new RangeError('A custom period needs both a start and an end date');
      return { from: custom.from, to: custom.to };
    }
  }
}

export type ComparisonKind = 'previous_period' | 'previous_year';

const isWholeMonths = (r: DateRange) => {
  const f = parse(r.from);
  const t = parse(r.to);
  return f.d === 1 && t.d === daysInMonth(t.y, t.m);
};

/**
 * The range to compare with.
 *
 * Previous year shifts both ends back twelve months (a 29 February clamps to
 * 28). Previous period is the same length ending the day before — except for
 * whole calendar months, which shift by the same number of months so that
 * "April–June" is compared with "January–March" and not with 91 odd days.
 */
export function comparisonRange(range: DateRange, kind: ComparisonKind): DateRange {
  if (kind === 'previous_year') {
    return { from: addMonths(range.from, -12), to: addMonths(range.to, -12) };
  }
  if (isWholeMonths(range)) {
    const f = parse(range.from);
    const t = parse(range.to);
    const months = (t.y - f.y) * 12 + (t.m - f.m) + 1;
    const from = addMonths(range.from, -months);
    const p = parse(addMonths(format(f.y, f.m, 1), -1));
    void p;
    return { from, to: addDays(range.from, -1) };
  }
  const length = daysBetween(range.from, range.to) + 1;
  return { from: addDays(range.from, -length), to: addDays(range.from, -1) };
}

/** Month starts (YYYY-MM-01) from the month of `from` to the month of `to`, inclusive. */
export function monthsInRange(range: DateRange): string[] {
  const out: string[] = [];
  let cursor = `${range.from.slice(0, 7)}-01`;
  const last = `${range.to.slice(0, 7)}-01`;
  while (cursor <= last && out.length < 600) {
    out.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return out;
}

/** Today's date in an IANA timezone as YYYY-MM-DD. */
export function todayIn(timezone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
    return /^\d{4}-\d{2}-\d{2}$/u.test(parts) ? parts : now.toISOString().slice(0, 10);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** The first month of the fiscal year: April for India, else January, unless configured. */
export function defaultFiscalStartMonth(configured: number | null | undefined, currency: string): number {
  if (configured && configured >= 1 && configured <= 12) return configured;
  return currency === 'INR' ? 4 : 1;
}

/** Index (1-12) of the fiscal month a date falls in. */
export function fiscalMonthIndex(date: string, startMonth: number): number {
  const { m } = parse(date);
  return ((m - startMonth + 12) % 12) + 1;
}
